import { mapValues } from 'lodash-es';
import { DateTime } from 'luxon';
import type { CommonDataEntity, DayDataEntities } from 'redux/modules/superreports.types';
import type { Currency, StartOfWeek } from 'types/account';
import { formatCost } from 'utils/currency';
import { getWeekNumber } from 'utils/date-helper';
import { durationFromHours } from 'utils/durations';

interface WidgetHoursFactoryParams {
  entities: DayDataEntities;
  startDate: string;
  cumulative: boolean;
  budget: boolean;
  startOfWeek?: StartOfWeek;
  currencyLookup: Record<string, Currency>;
}

export interface DataForDate {
  label: string;
  date: string;
  duration: number;
  cost: number;
  budget: boolean;
  estimated_duration: number;
  billedCost: number;
  unbilledCost: number;
  billedDuration: number;
  unbilledDuration: number;
  billableDuration: number;
  nonBillableDuration: number;
  currency?: Currency;
}

interface GroupedData {
  duration: number;
  estimated_duration: number;
  billed_duration: number;
  unbilled_duration: number;
  billable_duration: number;
  non_billable_duration: number;
  cost: number;
  billed_cost: number;
  unbilled_cost: number;
  currency_code?: string;
}

const groupData = (
  dayValues: DayDataEntities,
  dayKeys: Array<string>,
  dateFormat: string,
  currencyLookup: Record<string, Currency>,
  startOfWeek?: StartOfWeek
) => {
  const summed = dayKeys.reduce<Record<string, GroupedData>>((prev, key) => {
    const current = dayValues[key];
    const recordKey =
      startOfWeek != null
        ? `${getWeekNumber(key, startOfWeek)}-${DateTime.fromISO(current.day).year}`
        : DateTime.fromISO(current.day).toFormat(dateFormat);

    const durationValue = current.duration?.total_hours ?? 0;
    const costValue = current.cost?.amount ?? 0;
    const estimatedDurationValue = current.estimated_duration?.total_hours ?? 0;
    const billedDurationValue = current.billed_duration?.total_hours ?? 0;
    const unbilledDurationValue = current.unbilled_duration?.total_hours ?? 0;
    const billedCostValue = current.billed_cost?.amount ?? 0;
    const unbilledCostValue = current.unbilled_cost?.amount ?? 0;
    const billableDurationValue = current.billable_duration?.total_hours ?? 0;
    const nonBillableDurationValue = current.non_billable_duration?.total_hours ?? 0;
    const currencyCode = current.cost?.currency_code ?? current.billed_cost?.currency_code;

    return {
      ...prev,
      [recordKey]: {
        duration: (prev[recordKey]?.duration ?? 0) + durationValue,
        estimated_duration: (prev[recordKey]?.estimated_duration ?? 0) + estimatedDurationValue,
        billed_duration: (prev[recordKey]?.billed_duration ?? 0) + billedDurationValue,
        unbilled_duration: (prev[recordKey]?.unbilled_duration ?? 0) + unbilledDurationValue,
        billable_duration: (prev[recordKey]?.billable_duration ?? 0) + billableDurationValue,
        non_billable_duration:
          (prev[recordKey]?.non_billable_duration ?? 0) + nonBillableDurationValue,
        cost: (prev[recordKey]?.cost ?? 0) + costValue,
        billed_cost: (prev[recordKey]?.billed_cost ?? 0) + billedCostValue,
        unbilled_cost: (prev[recordKey]?.unbilled_cost ?? 0) + unbilledCostValue,
        currency_code: currencyCode,
      },
    };
  }, {});
  return mapValues(summed, value => {
    const currency = value.currency_code ? currencyLookup[value.currency_code] : undefined;
    return {
      duration: durationFromHours(value.duration),
      estimated_duration: durationFromHours(value.estimated_duration),
      billed_duration: durationFromHours(value.billed_duration),
      unbilled_duration: durationFromHours(value.unbilled_duration),
      billable_duration: durationFromHours(value.billable_duration),
      non_billable_duration: durationFromHours(value.non_billable_duration),
      cost: formatCost(value.cost, currency),
      billed_cost: formatCost(value.billed_cost, currency),
      unbilled_cost: formatCost(value.unbilled_cost, currency),
      currency,
    };
  });
};

const mapData = (
  numGroups: number,
  startDate: string,
  entities: Record<string, CommonDataEntity>,
  cumulative: boolean,
  budget: boolean,
  keyFormat: string,
  unit: 'days' | 'weeks' | 'months' | 'years',
  labelFormat: string,
  dateFormat: string,
  currencyLookup: Record<string, Currency>
) => {
  const data: Array<DataForDate> = [];
  if (!entities) return data;

  let cumulativeDuration = 0;
  let cumulativeCost = 0;

  for (let counter = 0; counter <= numGroups; counter++) {
    const day = DateTime.fromISO(startDate, { setZone: true }).plus({ [unit]: counter });

    const dayLabel = day.toFormat(labelFormat);
    const dayDate = day.toFormat(dateFormat);
    const dayKey = day.toFormat(keyFormat);
    const dayData = entities[dayKey];

    if (dayData) {
      const duration = dayData.duration?.total_hours ?? 0;
      const estimated_duration = dayData.estimated_duration?.total_hours ?? 0;
      const cost = dayData.cost?.amount ?? 0;
      const billedCost = dayData.billed_cost?.amount ?? 0;
      const unbilledCost = dayData.unbilled_cost?.amount ?? 0;
      const billedDuration = dayData.billed_duration?.total_hours ?? 0;
      const unbilledDuration = dayData.unbilled_duration?.total_hours ?? 0;
      const billableDuration = dayData.billable_duration?.total_hours ?? 0;
      const nonBillableDuration = dayData.non_billable_duration?.total_hours ?? 0;
      const currencyCode = dayData.cost?.currency_code ?? dayData.billed_cost?.currency_code;
      const currency = currencyCode ? currencyLookup[currencyCode] : undefined;

      cumulativeDuration += duration;
      cumulativeCost += cost;

      data.push({
        label: dayLabel,
        date: dayDate,
        duration: cumulative ? Math.round(cumulativeDuration) : duration,
        estimated_duration,
        cost: cumulative ? cumulativeCost : cost,
        budget,
        billedCost,
        unbilledCost,
        billedDuration,
        unbilledDuration,
        billableDuration,
        nonBillableDuration,
        currency,
      });
    } else {
      data.push({
        label: dayLabel,
        date: dayDate,
        duration: cumulative ? Math.round(cumulativeDuration) : 0,
        estimated_duration: 0,
        cost: cumulative ? cumulativeCost : 0,
        budget,
        billedCost: 0,
        unbilledCost: 0,
        billedDuration: 0,
        unbilledDuration: 0,
        billableDuration: 0,
        nonBillableDuration: 0,
      });
    }
  }

  return data;
};

export function reportWidgetHoursLineAsDaysFactory({
  entities,
  startDate,
  numDays,
  cumulative,
  budget,
  currencyLookup,
}: WidgetHoursFactoryParams & {
  numDays: number;
}) {
  return mapData(
    numDays,
    startDate,
    entities,
    cumulative,
    budget,
    'yyyy-MM-dd',
    'days',
    'd',
    'cccc, MMMM d, yyyy',
    currencyLookup
  );
}

export function reportWidgetHoursLineAsWeeksFactory({
  entities,
  result,
  startDate,
  numWeeks,
  cumulative,
  budget,
  startOfWeek,
  currencyLookup,
}: WidgetHoursFactoryParams & {
  result: Array<string>;
  numWeeks: number;
}) {
  const groupKey = 'WW-yyyy';
  const weeks = groupData(entities, result, groupKey, currencyLookup, startOfWeek);
  return mapData(
    numWeeks,
    startDate,
    weeks,
    cumulative,
    budget,
    groupKey,
    'weeks',
    "'W'W",
    "'Week' W, MMMM yyyy",
    currencyLookup
  );
}

export function reportWidgetHoursLineAsMonthsFactory({
  entities,
  result,
  startDate,
  numMonths,
  cumulative,
  budget,
  currencyLookup,
}: WidgetHoursFactoryParams & {
  result: Array<string>;
  numMonths: number;
}) {
  const groupKey = 'MM-yyyy';
  const months = groupData(entities, result, 'MM-yyyy', currencyLookup);
  return mapData(
    numMonths,
    startDate,
    months,
    cumulative,
    budget,
    groupKey,
    'months',
    'MMM',
    'MMMM, yyyy',
    currencyLookup
  );
}

export const reportWidgetHoursLineAsYearsFactory = ({
  entities,
  result,
  startDate,
  numYears,
  cumulative,
  budget,
  currencyLookup,
}: WidgetHoursFactoryParams & {
  result: Array<string>;
  numYears: number;
}) => {
  const groupKey = 'yyyy';
  const years = groupData(entities, result, 'yyyy', currencyLookup);
  return mapData(
    numYears,
    startDate,
    years,
    cumulative,
    budget,
    groupKey,
    'years',
    'yyyy',
    'yyyy',
    currencyLookup
  );
};
